import React from 'react';
import { format, parseISO } from 'date-fns';

const CallSheetPrintView = React.forwardRef(({ callSheet, isPageBreak = false }, ref) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div 
      ref={ref}
      className={`call-sheet-print bg-white text-black p-8 ${isPageBreak ? 'page-break-after' : ''}`}
      style={{ 
        width: '8.5in', 
        minHeight: '11in', 
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        lineHeight: '1.4',
        pageBreakAfter: isPageBreak ? 'always' : 'auto',
      }}
    >
      {/* Header */}
      <div style={{ borderBottom: '2px solid #000', paddingBottom: '12px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/avf-logo.jpg" alt="AV Factory" style={{ height: '50px', width: 'auto' }} />
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '10px', color: '#666', margin: 0 }}>www.avfactory.com</p>
          </div>
        </div>
      </div>

      {/* Title and Flex */}
      <div style={{ borderBottom: '3px solid #000', paddingBottom: '12px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
              CREW CALL SHEET
            </h1>
            <p style={{ fontSize: '14px', color: '#666', margin: '4px 0 0 0' }}>
              {formatDate(callSheet.date)}
            </p>
          </div>
          {callSheet.flex_number && (
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Flex #</p>
              <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                {callSheet.flex_number}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Event Name */}
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
          {callSheet.event_name}
        </h2>
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
        {/* Left Column - Venue & Room */}
        <div style={{ flex: 1 }}>
          <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
            <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
              Venue
            </p>
            <p style={{ fontWeight: 'bold', margin: 0, fontSize: '14px' }}>
              {callSheet.venue_name || callSheet.location_name || 'TBD'}
            </p>
            {(callSheet.venue_address || callSheet.location_address) && (
              <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                {callSheet.venue_address || callSheet.location_address}
                {(callSheet.venue_city || callSheet.location_city) && `, ${callSheet.venue_city || callSheet.location_city}`}
                {(callSheet.venue_state || callSheet.location_state) && ` ${callSheet.venue_state || callSheet.location_state}`}
                {(callSheet.venue_zip || callSheet.location_zip) && ` ${callSheet.venue_zip || callSheet.location_zip}`}
              </p>
            )}
            {callSheet.location_phone && (
              <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                📞 {callSheet.location_phone}
              </p>
            )}
          </div>
          
          {callSheet.room && (
            <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
              <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
                Room / Space
              </p>
              <p style={{ fontWeight: 'bold', margin: 0 }}>{callSheet.room}</p>
            </div>
          )}
        </div>

        {/* Right Column - Key Contacts */}
        <div style={{ flex: 1 }}>
          <div style={{ background: '#e8f5e9', padding: '12px', borderRadius: '4px', marginBottom: '8px' }}>
            <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
              Account Executive
            </p>
            <p style={{ fontWeight: 'bold', margin: 0 }}>
              {callSheet.account_exec_name || 'Not assigned'}
            </p>
            {callSheet.account_exec_phone && (
              <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>
                📞 {callSheet.account_exec_phone}
              </p>
            )}
          </div>

          <div style={{ background: '#e3f2fd', padding: '12px', borderRadius: '4px', marginBottom: '8px' }}>
            <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
              Project Manager
            </p>
            <p style={{ fontWeight: 'bold', margin: 0 }}>
              {callSheet.project_manager_name || 'Not assigned'}
            </p>
            {callSheet.project_manager_phone && (
              <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>
                📞 {callSheet.project_manager_phone}
              </p>
            )}
          </div>

          <div style={{ background: '#fff3e0', padding: '12px', borderRadius: '4px' }}>
            <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
              On-Call Warehouse Tech
            </p>
            <p style={{ fontWeight: 'bold', margin: 0 }}>
              {callSheet.warehouse_tech_name || 'Not assigned'}
            </p>
            {callSheet.warehouse_tech_phone && (
              <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>
                📞 {callSheet.warehouse_tech_phone}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Crew Details Row */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        {callSheet.crew_meeting_location && (
          <div style={{ flex: 1, background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
            <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
              Crew Meeting Location
            </p>
            <p style={{ fontWeight: 'bold', margin: 0 }}>{callSheet.crew_meeting_location}</p>
          </div>
        )}
        
        {callSheet.dress_code && (
          <div style={{ flex: 1, background: '#f3e5f5', padding: '12px', borderRadius: '4px' }}>
            <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
              Dress Code
            </p>
            <p style={{ fontWeight: 'bold', margin: 0 }}>{callSheet.dress_code}</p>
          </div>
        )}
      </div>

      {/* Meal Info */}
      {callSheet.meal_info && (
        <div style={{ background: '#fff8e1', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
          <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
            Meal Information
          </p>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{callSheet.meal_info}</p>
        </div>
      )}

      {/* Schedule */}
      {callSheet.schedule_items?.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 'bold', margin: '0 0 8px 0', borderBottom: '1px solid #ddd', paddingBottom: '4px' }}>
            Schedule
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign: 'left', padding: '8px', fontSize: '11px', fontWeight: 'bold', width: '80px' }}>
                  Time
                </th>
                <th style={{ textAlign: 'left', padding: '8px', fontSize: '11px', fontWeight: 'bold' }}>
                  Activity
                </th>
              </tr>
            </thead>
            <tbody>
              {callSheet.schedule_items.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px', fontFamily: 'monospace', fontWeight: 'bold' }}>
                    {item.time || '--:--'}
                  </td>
                  <td style={{ padding: '8px' }}>
                    {item.action || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes */}
      {callSheet.notes && (
        <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
          <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
            Notes
          </p>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{callSheet.notes}</p>
        </div>
      )}

      {/* Bottom Section - Parking & Emergency */}
      <div style={{ display: 'flex', gap: '16px', marginTop: 'auto' }}>
        {/* Parking */}
        <div style={{ flex: 1 }}>
          <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
            <p style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', margin: '0 0 4px 0' }}>
              Parking
            </p>
            {callSheet.parking_options ? (
              <p style={{ margin: 0, fontSize: '11px' }}>{callSheet.parking_options}</p>
            ) : (
              <p style={{ margin: 0, fontSize: '11px', color: '#999' }}>Not specified</p>
            )}
            <p style={{ margin: '8px 0 0 0', fontSize: '10px', fontStyle: 'italic', color: '#666' }}>
              ⚠️ Vehicles are parked at owners risk. Parking is not reimbursed unless noted.
            </p>
          </div>
        </div>

        {/* Emergency Room */}
        <div style={{ flex: 1 }}>
          <div style={{ background: '#ffebee', padding: '12px', borderRadius: '4px', border: '1px solid #ffcdd2' }}>
            <p style={{ fontSize: '10px', color: '#c62828', textTransform: 'uppercase', margin: '0 0 4px 0', fontWeight: 'bold' }}>
              🏥 Nearest Emergency Room
            </p>
            {callSheet.nearest_er_name ? (
              <>
                <p style={{ fontWeight: 'bold', margin: 0 }}>{callSheet.nearest_er_name}</p>
                {callSheet.nearest_er_address && (
                  <p style={{ margin: '2px 0 0 0', fontSize: '11px' }}>{callSheet.nearest_er_address}</p>
                )}
                {callSheet.nearest_er_phone && (
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', fontWeight: 'bold' }}>
                    📞 {callSheet.nearest_er_phone}
                  </p>
                )}
              </>
            ) : (
              <p style={{ margin: 0, fontSize: '11px', color: '#999' }}>Not specified</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

CallSheetPrintView.displayName = 'CallSheetPrintView';

export default CallSheetPrintView;
